// Threshold bench — pick a real frame, see every number the engine produced from it, and
// drag the thresholds until the verdict matches what your eyes say about the photo.
//
// The Frame Quality page answers "how much of what we captured was usable?". This answers
// the question that follows: "would THIS photo have passed, and what would it take to change
// that?" Choosing a cut-off from a funnel alone means guessing; here the photo is on screen
// next to the number, so a threshold can be argued about instead of assumed.
//
// WHY THE SLIDERS ARE INSTANT: scoring a frame returns every raw metric at once, and almost
// every threshold is just a comparison against one of them. So dragging re-evaluates the
// verdict in the browser with no round trip. Only the model's own conf/iou would need a
// re-score, and those are not exposed here — they change what the engine detects, which is a
// different question from where the gates should sit.
//
// THE SLIDERS ARE THE REAL CONTROL. These thresholds used to be environment variables spread
// across three stacks, so tuning one meant a redeploy by whoever held Portainer access — which
// in practice meant they were never tuned and this page could only simulate. They now live in
// DMS and are applied per request, so "Apply to production" takes effect on the next frame.
//
// Not every slider gates something, and the page says which is which rather than letting a
// preference masquerade as a gate — see the `applied` map from the server:
//   live        — changes what production does, now
//   dms-only    — changes this dashboard's funnel; Planomaster still reads its own env var
//   simulation  — nothing in production consumes it yet
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Card, Row, Col, Select, Button, Slider, Tag, Alert, Spin, Empty, Typography,
  Descriptions, Switch, message, Space,
} from "antd";
import { ExperimentOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  getCameras,
  getPictures,
  getFrameQualityBench,
  getThresholds,
  saveThresholds,
} from "../network/api";

const { Title, Text, Paragraph } = Typography;

const GOOD = "#52c41a";
const CRIT = "#ff4d4f";
const MUTED = "#8c9bab";

// Fallbacks only. The real values come from GET /frame-quality/thresholds on mount; these
// exist so the page renders something sane if that call fails, and match the server's own
// seed defaults so a failed load cannot show a different baseline than production runs.
const DEFAULTS = {
  chillerGateThreshold: 0.7,
  chillerAreaThreshold: 0.3,
  sharpnessThreshold: 150,
  humanExclusionScore: 0.5,
  maskFrameFill: 0.0,
  maskSolidity: 0.0,
  rejectCutOff: false,
  cutOffMinFill: 0.0,
  cutOffMaxLeft: 1.0,
  cutOffMaxRight: 1.0,
  cutOffFillExempt: 0.0,
  cropPadFrac: 0.06,
  maxFacingSkew: 0,
  humanOnCropEnabled: false,
  focusFirstInBand: false,
  skuPreselectEnabled: false,
  skuPreselectTopN: 3,
  cropToChillerEnabled: false,
  minSkuToDeliver: 0,
  humanPhantomBoxFrac: 0.9,
  humanPhantomMaxScore: 0.6,
  humanConf: 0.9,
  humanMargin: 0.08,
  humanScanConf: 0.25,
  humanRedactEnabled: false,
};

const pct = (x) => (x == null ? "—" : `${(x * 100).toFixed(1)}%`);
const num = (x, d = 3) => (x == null ? "—" : Number(x).toFixed(d));

// Fraction of the frame the redaction would destroy, counting overlap ONCE.
//
// Exact, via coordinate compression rather than summing box areas: overlapping detections
// are common on this model and summing them reports more damage than the redactor does,
// which would push whoever is tuning toward a threshold higher than the evidence warrants.
// O(n^3) in box count, and n is single digits.
function unionFraction(boxes, w, h) {
  if (!boxes.length || !w || !h) return 0;
  const xs = [...new Set(boxes.flatMap((b) => [b.x1, b.x2]))].sort((a, b) => a - b);
  const ys = [...new Set(boxes.flatMap((b) => [b.y1, b.y2]))].sort((a, b) => a - b);
  let area = 0;
  for (let i = 0; i < xs.length - 1; i++) {
    for (let j = 0; j < ys.length - 1; j++) {
      const cx = (xs[i] + xs[i + 1]) / 2;
      const cy = (ys[j] + ys[j + 1]) / 2;
      if (boxes.some((b) => cx >= b.x1 && cx <= b.x2 && cy >= b.y1 && cy <= b.y2)) {
        area += (xs[i + 1] - xs[i]) * (ys[j + 1] - ys[j]);
      }
    }
  }
  return area / (w * h);
}

/** Apply the current slider positions to one reading. Pure — this is the whole "engine".
 * `focusScore` is the frame's variance-of-Laplacian sharpness (a sibling of the gateway
 * reading, measured by DMS), so the blur gate can be shown alongside the chiller checks.
 * `humanMaxScore` is the highest person score the screener recorded for the frame, so the
 * occlusion gate — the one that keeps a shopper standing at the shelf from winning — can be
 * shown too. */
function evaluate(reading, t, focusScore, humanMaxScore) {
  if (!reading) return null;
  const mask = reading.mask || null;
  const checks = [
    {
      key: "detected",
      label: "Chiller detected",
      pass: Boolean(reading.found),
      detail: reading.tooDark
        ? "frame too dark to score"
        : reading.smallBox
        ? "box too small to be a real chiller"
        : reading.found
        ? "found"
        : "no chiller found",
    },
    {
      key: "confidence",
      label: `Confidence ≥ ${t.chillerGateThreshold.toFixed(2)}`,
      pass: (reading.confidence ?? 0) >= t.chillerGateThreshold,
      detail: num(reading.confidence),
    },
    {
      key: "area",
      label: `Box covers ≥ ${pct(t.chillerAreaThreshold)} of frame`,
      pass: (reading.areaFraction ?? 0) >= t.chillerAreaThreshold,
      detail: pct(reading.areaFraction),
    },
  ];

  // Sharpness gate — the one the chiller score can't enforce. Only shown when the frame has a
  // focus score (DMS scores it at ingest) and the floor is on; "not measured" must never read
  // as "passed", same rule as the mask checks.
  if (t.sharpnessThreshold > 0 && focusScore != null) {
    checks.push({
      key: "sharpness",
      label: `Sharp enough (focus ≥ ${Math.round(t.sharpnessThreshold)})`,
      pass: focusScore >= t.sharpnessThreshold,
      detail: Math.round(focusScore),
    });
  }

  // Human-occlusion gate — the one that keeps a shopper reaching into the chiller from winning
  // selection. Gates on the raw person score (humanMaxScore), NOT humanDetected, because
  // humanDetected only flips at the redaction bar (~0.9) and a person at 0.6–0.89 still blocks
  // the shelf. Only shown when the frame carries a person score and the gate is on; "not
  // screened" (null) must never read as "passed", same rule as sharpness and the mask checks.
  if (t.humanExclusionScore > 0 && t.humanExclusionScore <= 1 && humanMaxScore != null) {
    checks.push({
      key: "human",
      label: `No one blocking the shelf (person < ${t.humanExclusionScore.toFixed(2)})`,
      pass: humanMaxScore < t.humanExclusionScore,
      detail: `person ${num(humanMaxScore, 2)}`,
    });
  }

  // Mask checks only exist when a segmentation model produced an outline. A box-only model
  // (or a rollback) reports null, and "not measured" must never quietly read as "passed".
  if (mask) {
    if (t.maskFrameFill > 0) {
      checks.push({
        key: "frameFill",
        label: `Chiller fills ≥ ${pct(t.maskFrameFill)} of frame`,
        pass: (mask.frameFill ?? 0) >= t.maskFrameFill,
        detail: pct(mask.frameFill),
      });
    }
    if (t.maskSolidity > 0) {
      checks.push({
        key: "solidity",
        label: `Solidity ≥ ${t.maskSolidity.toFixed(2)} (nothing blocking it)`,
        pass: (mask.solidity ?? 0) >= t.maskSolidity,
        detail: num(mask.solidity),
      });
    }
    if (t.rejectCutOff) {
      const cut = mask.cutOffSides || [];
      // A single clipped edge is negotiable when the chiller still fills at least cutOffMinFill
      // of the frame (mirrors api/Picture.js). Two+ sides are never forgiven; 0 = strict.
      const forgivenSlight =
        cut.length === 1 &&
        t.cutOffMinFill > 0 &&
        (mask.frameFill ?? 0) >= t.cutOffMinFill;
      checks.push({
        key: "cutOff",
        label: "Whole chiller in shot",
        pass: cut.length === 0 || forgivenSlight,
        detail: cut.length
          ? forgivenSlight
            ? `cut off: ${cut.join(", ")} — forgiven (fills ${num(mask.frameFill)})`
            : `cut off: ${cut.join(", ")}`
          : "not cut off",
      });
    }
  }

  return { checks, accepted: checks.every((c) => c.pass), maskAvailable: Boolean(mask) };
}

const FrameQualityBench = () => {
  const [cameras, setCameras] = useState([]);
  const [deviceId, setDeviceId] = useState(null);
  const [frames, setFrames] = useState([]);
  const [pictureId, setPictureId] = useState(null);
  const [loadingFrames, setLoadingFrames] = useState(false);
  const [scoring, setScoring] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [t, setT] = useState(DEFAULTS);
  // What production is running, as last loaded or saved. Kept beside the working copy so
  // the page can show what is actually live and whether the sliders still differ from it —
  // otherwise a half-finished edit is indistinguishable from the deployed setting.
  const [saved, setSaved] = useState(null);
  const [applied, setApplied] = useState({});
  const [saving, setSaving] = useState(false);

  const loadThresholds = useCallback(async () => {
    try {
      const r = await getThresholds();
      setSaved(r.thresholds);
      setApplied(r.applied || {});
      setT((s) => ({ ...s, ...r.thresholds }));
    } catch {
      message.error("Could not load the saved thresholds — showing defaults");
    }
  }, []);

  useEffect(() => {
    loadThresholds();
  }, [loadThresholds]);

  const dirty = useMemo(() => {
    if (!saved) return false;
    return Object.keys(saved).some((k) => saved[k] !== t[k]);
  }, [saved, t]);

  const apply = useCallback(async () => {
    setSaving(true);
    try {
      // Send only what changed, so two people tuning different knobs cannot clobber each
      // other by each PUTting a whole snapshot.
      const patch = Object.fromEntries(
        Object.keys(saved || {}).filter((k) => saved[k] !== t[k]).map((k) => [k, t[k]])
      );
      const r = await saveThresholds(patch);
      setSaved(
        Object.fromEntries(Object.keys(saved).map((k) => [k, r.thresholds[k]]))
      );
      message.success(`Applied to production: ${r.changed.join(", ")}`);
    } catch (err) {
      message.error(
        err?.response?.data?.error || "Could not save — hardwareAdmin or above is required"
      );
    } finally {
      setSaving(false);
    }
  }, [saved, t]);

  useEffect(() => {
    // getCameras already unwraps the { data, total } envelope to a plain array; keep the
    // object form as a fallback so this survives either shape.
    getCameras(1, 200, {})
      .then((r) => {
        const rows = Array.isArray(r) ? r : r?.data || [];
        setCameras(rows.filter((c) => !c.archivedAt));
      })
      .catch(() => message.error("Could not load the device list"));
  }, []);

  const loadFrames = useCallback(async (dev) => {
    if (!dev) return;
    setLoadingFrames(true);
    setFrames([]);
    setPictureId(null);
    setResult(null);
    setError(null);
    try {
      const r = await getPictures(dev, 25);
      const rows = Array.isArray(r) ? r : r?.data || [];
      setFrames(rows);
      if (rows.length) setPictureId(rows[0]._id);
    } catch {
      message.error("Could not load frames for this device");
    } finally {
      setLoadingFrames(false);
    }
  }, []);

  const score = useCallback(async () => {
    if (!pictureId) return;
    setScoring(true);
    setError(null);
    try {
      // The saved thresholds are the authority now, so scoring a frame must NOT move the
      // sliders — doing so would silently discard an edit in progress every time the
      // operator re-scored to see its effect, which is the main thing they do here.
      setResult(await getFrameQualityBench(pictureId));
    } catch (err) {
      setResult(null);
      setError(err?.response?.data?.error || err.message || "Scoring failed");
    } finally {
      setScoring(false);
    }
  }, [pictureId]);

  // Recomputed on every slider drag — no request, because the reading already holds
  // every number these thresholds compare against.
  const verdict = useMemo(
    () => evaluate(result?.reading, t, result?.focusScore, result?.recorded?.humanMaxScore),
    [result, t]
  );

  // Same trick as the chiller gates: the server returned every candidate box down to a floor
  // of ~0.05, so moving the redaction threshold is a filter over a list already in memory.
  const redaction = useMemo(() => {
    const h = result?.human;
    if (!h) return null;
    const keep = (h.candidates || []).filter((b) => b.score >= t.humanConf);
    const w = h.imageSize?.w || 0;
    const ht = h.imageSize?.h || 0;
    return {
      keep,
      w,
      h: ht,
      areaFraction: unionFraction(keep, w, ht),
      // Candidates that sit below the current threshold — what this setting is letting past.
      below: (h.candidates || []).filter((b) => b.score < t.humanConf),
      droppedDegenerate: h.droppedDegenerate ?? 0,
      model: h.humanModel,
      productionConf: h.productionConf,
    };
  }, [result, t.humanConf]);

  const selectedFrame = frames.find((f) => f._id === pictureId);
  const mask = result?.reading?.mask;

  return (
    <div style={{ padding: 16 }}>
      <Title level={4} style={{ marginBottom: 4 }}>
        <ExperimentOutlined /> Threshold Bench
      </Title>
      <Paragraph type="secondary" style={{ maxWidth: 760 }}>
        Score one real frame, then move the thresholds until the verdict agrees with what you
        see in the photo — and apply them when it does. These are the values production runs
        on, so a cut-off can be chosen against a photo rather than guessed, and changed here
        rather than in a redeploy. Nothing is applied until you press{" "}
        <Text strong>Apply to production</Text>.
      </Paragraph>

      <Row gutter={16}>
        <Col xs={24} lg={10}>
          <Card size="small" title="Pick a frame">
            <Space direction="vertical" style={{ width: "100%" }} size={10}>
              <Select
                showSearch
                style={{ width: "100%" }}
                placeholder="Device"
                value={deviceId}
                onChange={(v) => {
                  setDeviceId(v);
                  loadFrames(v);
                }}
                filterOption={(i, o) => (o?.label ?? "").toLowerCase().includes(i.toLowerCase())}
                options={cameras.map((c) => ({
                  value: c.deviceId,
                  label: `${c.deviceId}${c.shop?.name ? ` — ${c.shop.name}` : ""}`,
                }))}
              />
              <Select
                style={{ width: "100%" }}
                placeholder={loadingFrames ? "Loading…" : "Frame (newest first)"}
                value={pictureId}
                onChange={(v) => {
                  setPictureId(v);
                  setResult(null);
                }}
                loading={loadingFrames}
                disabled={!frames.length}
                options={frames.map((f) => ({
                  value: f._id,
                  label: `${dayjs(f.createdAt).format("DD MMM HH:mm:ss")}${
                    f.chillerConfidence != null ? ` · recorded ${num(f.chillerConfidence, 2)}` : ""
                  }`,
                }))}
              />
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                loading={scoring}
                disabled={!pictureId}
                onClick={score}
                block
              >
                Score this frame
              </Button>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Runs a real inference (~0.4s). Nothing is saved.
              </Text>
            </Space>

            {selectedFrame?.absolutePath && (
              <div style={{ position: "relative", marginTop: 12, lineHeight: 0 }}>
                <img
                  src={selectedFrame.absolutePath}
                  alt="selected frame"
                  style={{ width: "100%", borderRadius: 4, border: "1px solid #303030" }}
                />
                {/* What the redactor would destroy, drawn ON the photo. The number alone is
                    not enough for this model: it false-positives on bare shelving, so
                    "43% would be blurred" only becomes a decision once you can see that the
                    43% is the chiller and not a customer. */}
                {redaction?.keep.map((b, i) => (
                  <div
                    key={`r${i}`}
                    title={`would be blurred — score ${num(b.score, 3)}`}
                    style={{
                      position: "absolute",
                      left: `${(b.x1 / redaction.w) * 100}%`,
                      top: `${(b.y1 / redaction.h) * 100}%`,
                      width: `${((b.x2 - b.x1) / redaction.w) * 100}%`,
                      height: `${((b.y2 - b.y1) / redaction.h) * 100}%`,
                      border: `2px solid ${CRIT}`,
                      background: `${CRIT}33`,
                      boxSizing: "border-box",
                    }}
                  />
                ))}
                {/* Below the threshold: what this setting is choosing to let through. Drawn
                    faintly because it is the other half of the trade — every one of these is
                    either shelf you kept or a person you missed. */}
                {redaction?.below.map((b, i) => (
                  <div
                    key={`b${i}`}
                    title={`below threshold — score ${num(b.score, 3)}`}
                    style={{
                      position: "absolute",
                      left: `${(b.x1 / redaction.w) * 100}%`,
                      top: `${(b.y1 / redaction.h) * 100}%`,
                      width: `${((b.x2 - b.x1) / redaction.w) * 100}%`,
                      height: `${((b.y2 - b.y1) / redaction.h) * 100}%`,
                      border: `1px dashed ${MUTED}`,
                      boxSizing: "border-box",
                    }}
                  />
                ))}
              </div>
            )}
            {redaction && (
              <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 6 }}>
                <span style={{ color: CRIT }}>■</span> would be blurred ·{" "}
                <span style={{ color: MUTED }}>▫</span> below threshold
              </Text>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={14}>
          <Card
            size="small"
            title="Thresholds"
            extra={
              <Space>
                <Button size="small" disabled={!dirty || !saved} onClick={() => setT((s) => ({ ...s, ...saved }))}>
                  Discard changes
                </Button>
                <Button
                  size="small"
                  type="primary"
                  loading={saving}
                  disabled={!dirty}
                  onClick={apply}
                >
                  Apply to production
                </Button>
              </Space>
            }
          >
            {dirty && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 10 }}
                message="Unsaved changes"
                description="The sliders below differ from what production is running. Nothing changes until you apply."
              />
            )}
            <SliderRow
              label="Chiller gate confidence"
              hint="The bar a frame must clear to be the one compliance runs on, and this dashboard's usable cut-off."
              badge={applied.chillerGateThreshold}
              value={t.chillerGateThreshold}
              live={saved?.chillerGateThreshold}
              onChange={(v) => setT((s) => ({ ...s, chillerGateThreshold: v }))}
              max={1}
            />
            <SliderRow
              label="Box area of frame"
              hint="Rejects slivers that can't be a real chiller. Sent to the engine on every ingest."
              badge={applied.chillerAreaThreshold}
              value={t.chillerAreaThreshold}
              live={saved?.chillerAreaThreshold}
              onChange={(v) => setT((s) => ({ ...s, chillerAreaThreshold: v }))}
              max={1}
            />
            <SliderRow
              label="Sharpness (focus) floor"
              hint="Variance-of-Laplacian focus. Rejects blurred / fogged frames that still carry a high chiller score — the gate confidence can't see. Sharp label-legible shelves run 600+; blur sits under 100. 0 turns it off."
              badge={applied.sharpnessThreshold}
              value={t.sharpnessThreshold}
              live={saved?.sharpnessThreshold}
              onChange={(v) => setT((s) => ({ ...s, sharpnessThreshold: v }))}
              max={2000}
              step={10}
              fmt={(v) => Math.round(v)}
            />
            <SliderRow
              label="Person occlusion cut-off"
              hint="Rejects a compliance frame when a person was seen in it at or above this score — a shopper reaching into the chiller blocks the shelf while the fridge behind them still scores high. Separate from the redaction bar below (0.9): that one destroys pixels, this one only drops the frame from selection. Lower = stricter. 1 turns it off."
              badge={applied.humanExclusionScore}
              value={t.humanExclusionScore}
              live={saved?.humanExclusionScore}
              onChange={(v) => setT((s) => ({ ...s, humanExclusionScore: v }))}
              max={1}
            />
            <div style={{ borderTop: "1px solid #303030", margin: "14px 0 10px" }} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Mask checks — only available on a segmentation model, and ungated in production
              today. Leave at 0 to ignore.
            </Text>
            <SliderRow
              label="Chiller fills ≥ of frame"
              hint="The mask itself, not its bounding box"
              badge={applied.maskFrameFill}
              value={t.maskFrameFill}
              live={saved?.maskFrameFill}
              onChange={(v) => setT((s) => ({ ...s, maskFrameFill: v }))}
              max={1}
              disabled={!mask}
            />
            <SliderRow
              label="Solidity"
              hint="Drops when a person or trolley blocks the unit"
              badge={applied.maskSolidity}
              value={t.maskSolidity}
              live={saved?.maskSolidity}
              onChange={(v) => setT((s) => ({ ...s, maskSolidity: v }))}
              max={1}
              disabled={!mask}
            />
            <Row align="middle" style={{ marginTop: 10 }}>
              <Col flex="auto">
                <Text>Reject when the chiller is cut off at a side</Text>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Left/right only — these cameras fill the frame vertically by design
                  </Text>
                </div>
              </Col>
              <Col>
                <Switch
                  checked={t.rejectCutOff}
                  disabled={!mask}
                  onChange={(v) => setT((s) => ({ ...s, rejectCutOff: v }))}
                />
              </Col>
            </Row>
            <SliderRow
              label="Forgive a slight cut-off when chiller fills ≥"
              hint="Only a SINGLE clipped edge is forgiven, and only when the chiller still fills at least this much of the frame — a barely-cropped but well-framed shelf stays usable. Two or more cut sides are always rejected. 0 = reject any cut-off."
              badge={applied.cutOffMinFill}
              value={t.cutOffMinFill}
              live={saved?.cutOffMinFill}
              onChange={(v) => setT((s) => ({ ...s, cutOffMinFill: v }))}
              max={1}
              disabled={!mask || !t.rejectCutOff}
            />
            <div style={{ borderTop: "1px solid #303030", margin: "12px 0 6px" }} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Cut-off by magnitude — reject when the chiller runs off a side by more than this fraction
              (the real "how much is cut"). A fisheye sliver is ~0 and passes; a half-cut chiller is
              0.5–0.9 and is rejected. Top/bottom are never gated (these cameras fill vertically). 1 = off.
            </Text>
            <SliderRow
              label="Reject when LEFT edge cut >"
              hint="Chiller running off the LEFT edge by more than this fraction is rejected. ~0.2 keeps fisheye slivers but drops half-cut chillers. 1 = off."
              badge={applied.cutOffMaxLeft}
              value={t.cutOffMaxLeft}
              live={saved?.cutOffMaxLeft}
              onChange={(v) => setT((s) => ({ ...s, cutOffMaxLeft: v }))}
              max={1}
              disabled={!mask}
            />
            <SliderRow
              label="Reject when RIGHT edge cut >"
              hint="Chiller running off the RIGHT edge by more than this fraction is rejected. 1 = off."
              badge={applied.cutOffMaxRight}
              value={t.cutOffMaxRight}
              live={saved?.cutOffMaxRight}
              onChange={(v) => setT((s) => ({ ...s, cutOffMaxRight: v }))}
              max={1}
              disabled={!mask}
            />
            <SliderRow
              label="Forgive an edge cut-off when chiller fills ≥"
              hint="Fill-aware exemption (O105): a full chiller that merely TOUCHES the frame edge (fisheye units whose body reaches the border) is not cut off — it just fills to the edge. A genuinely half-cut chiller shows less of itself, so its fill is lower. When set, an over-tolerance LEFT/RIGHT edge is forgiven if the fill is at least this high — letting the tolerances above stay strict for real cut-offs. 0 = off."
              badge={applied.cutOffFillExempt}
              value={t.cutOffFillExempt}
              live={saved?.cutOffFillExempt}
              onChange={(v) => setT((s) => ({ ...s, cutOffFillExempt: v }))}
              max={1}
              disabled={!mask}
            />
            <SliderRow
              label="Crop padding around the chiller"
              hint="Padding added around the chiller box for the delivered crop AND the person-in-crop check, as a fraction of the box size per side. A little breathing room so the chiller edge is not shaved. Because the human check uses the same padded box, a person who lands inside the padded delivered crop is still caught. Typical ~0.06."
              badge={applied.cropPadFrac}
              value={t.cropPadFrac}
              live={saved?.cropPadFrac}
              onChange={(v) => setT((s) => ({ ...s, cropPadFrac: v }))}
              max={0.5}
            />
            <SliderRow
              label="Reject when chiller angled (facing skew) >"
              hint="Rejects a FULLY in-frame chiller viewed too far off square (a perspective trapezoid). Skew is a log height-ratio: 0 = face-on, larger = more angled. 0 = off. INERT until the MarketEye gateway emits the facing signal."
              badge={applied.maxFacingSkew}
              value={t.maxFacingSkew}
              live={saved?.maxFacingSkew}
              onChange={(v) => setT((s) => ({ ...s, maxFacingSkew: v }))}
              max={2}
              disabled={!mask}
            />
          </Card>

          <Card size="small" title="SKU pre-select & chiller crop" style={{ marginTop: 16 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Both call the gateway once per finalist (~1.3s each) and are best-effort — a gateway
              hiccup falls back to the normal pick / the uncropped frame. Off by default.
            </Text>
            <Row align="middle" style={{ marginTop: 12 }}>
              <Col flex="auto">
                <Text>Judge people on the cropped chiller (crop-first human)</Text>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Crops each finalist to the chiller and runs person detection on the crop, so a
                    shopper standing BESIDE the unit is cropped away and no longer blocks the frame —
                    only someone in front of the shelves does. Adds a detect call per finalist.
                  </Text>
                </div>
              </Col>
              <Col>
                <Switch
                  checked={t.humanOnCropEnabled}
                  onChange={(v) => setT((s) => ({ ...s, humanOnCropEnabled: v }))}
                />
              </Col>
            </Row>
            <Row align="middle" style={{ marginTop: 12 }}>
              <Col flex="auto">
                <Text>Prefer the sharpest frame (focus first)</Text>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Among equally well-framed candidates, rank the sharpest first so the winner — and
                    the SKU-preselect pool — favours crisp frames over the last hundredths of chiller
                    confidence.
                  </Text>
                </div>
              </Col>
              <Col>
                <Switch
                  checked={t.focusFirstInBand}
                  onChange={(v) => setT((s) => ({ ...s, focusFirstInBand: v }))}
                />
              </Col>
            </Row>
            <Row align="middle" style={{ marginTop: 12 }}>
              <Col flex="auto">
                <Text>Pick the best-stocked frame (SKU pre-select)</Text>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Re-ranks the top candidates that already passed every gate by how many SKUs are
                    detected. Humans and the other gates still filter first.
                  </Text>
                </div>
              </Col>
              <Col>
                <Switch
                  checked={t.skuPreselectEnabled}
                  onChange={(v) => setT((s) => ({ ...s, skuPreselectEnabled: v }))}
                />
              </Col>
            </Row>
            <SliderRow
              label="Candidates to score for SKUs"
              hint="How many of the top eligible frames to run /analyze on. Higher = more accurate, more latency. 1 effectively disables the re-rank."
              badge={applied.skuPreselectTopN}
              value={t.skuPreselectTopN}
              live={saved?.skuPreselectTopN}
              onChange={(v) => setT((s) => ({ ...s, skuPreselectTopN: v }))}
              max={10}
              step={1}
              fmt={(v) => String(Math.round(v))}
              disabled={!t.skuPreselectEnabled}
            />
            <SliderRow
              label="Minimum SKUs to deliver"
              hint="Never send a compliance master with fewer detected products than this. A chiller a customer is standing in front of (or an empty one) reads ~0 SKUs — this catches the occlusion the person gate misses when the detector under-scores a close-up back. 0 = off. Typical 2–5."
              badge={applied.minSkuToDeliver}
              value={t.minSkuToDeliver}
              live={saved?.minSkuToDeliver}
              onChange={(v) => setT((s) => ({ ...s, minSkuToDeliver: v }))}
              max={20}
              step={1}
              fmt={(v) => String(Math.round(v))}
            />
            <SliderRow
              label="Phantom human box: frame coverage"
              hint="A 'person' box covering at least this fraction of the WHOLE frame is treated as detector noise (not a real person) in the person-in-crop test — the human model intermittently fires a low-confidence full-frame box on a clean, empty chiller and falsely blocks a good frame. 1 = off (never treat any box as phantom). Default 0.90."
              badge={applied.humanPhantomBoxFrac}
              value={t.humanPhantomBoxFrac}
              live={saved?.humanPhantomBoxFrac}
              onChange={(v) => setT((s) => ({ ...s, humanPhantomBoxFrac: v }))}
              max={1}
              step={0.05}
            />
            <SliderRow
              label="Phantom human box: score ceiling"
              hint="A near-full-frame box is only dropped as phantom when its score is BELOW this. A genuine close-up person (a real body against the glass) scores above it and still blocks the frame. 0 = never treat any box as phantom (filter off). Default 0.60."
              badge={applied.humanPhantomMaxScore}
              value={t.humanPhantomMaxScore}
              live={saved?.humanPhantomMaxScore}
              onChange={(v) => setT((s) => ({ ...s, humanPhantomMaxScore: v }))}
              max={1}
              step={0.05}
            />
            <Row align="middle" style={{ marginTop: 12 }}>
              <Col flex="auto">
                <Text>Crop the delivered image to the chiller</Text>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Trims the master to the chiller's bounding box so a shopper beside the unit falls
                    outside the frame. Does not remove someone standing in front of the shelves.
                  </Text>
                </div>
              </Col>
              <Col>
                <Switch
                  checked={t.cropToChillerEnabled}
                  onChange={(v) => setT((s) => ({ ...s, cropToChillerEnabled: v }))}
                />
              </Col>
            </Row>
          </Card>

          <Card size="small" title="Person redaction" style={{ marginTop: 16 }}>
            {result?.humanError && (
              <Alert
                type="warning"
                showIcon
                message="Could not measure person detection on this frame"
                description={`${result.humanError} — this is not the same as "no people here". Do not tune against it.`}
              />
            )}
            {!result && <Empty description="Score a frame to see what would be blurred" />}
            {redaction && (
              <>
                {result?.recorded?.humanRedacted && (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="This frame was already redacted at ingest"
                    description={`It was blurred at ${num(result.recorded.humanConf, 2)} before being stored, so you are tuning against the redacted copy, not the original. Pick a frame with no redaction to judge a threshold honestly.`}
                  />
                )}
                <Row align="middle" style={{ marginBottom: 6 }}>
                  <Col flex="auto">
                    <Text strong>Redact person regions at ingest</Text>{" "}
                    <Tag color={t.humanRedactEnabled ? "error" : "default"} style={{ fontSize: 10 }}>
                      {t.humanRedactEnabled ? "destroying pixels" : "off"}
                    </Tag>
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Off stores every frame unmodified and still records the verdict — which
                        is how you shadow-run this without destroying anything.
                      </Text>
                    </div>
                  </Col>
                  <Col>
                    <Switch
                      checked={t.humanRedactEnabled}
                      onChange={(v) => setT((s) => ({ ...s, humanRedactEnabled: v }))}
                    />
                  </Col>
                </Row>
                <SliderRow
                  label="Blur regions scoring ≥"
                  hint="Lower = blur more of the frame, permanently. This model false-positives on bare shelving, so check the overlay before lowering it."
                  badge={applied.humanConf}
                  value={t.humanConf}
                  live={saved?.humanConf}
                  onChange={(v) => setT((s) => ({ ...s, humanConf: v }))}
                  max={1}
                />
                <SliderRow
                  label="Box margin"
                  hint="Grows each blurred region by this fraction per side, so a tight box does not leak hair or the edge of a face."
                  badge={applied.humanMargin}
                  value={t.humanMargin}
                  live={saved?.humanMargin}
                  onChange={(v) => setT((s) => ({ ...s, humanMargin: v }))}
                  max={0.5}
                />
                <SliderRow
                  label="Record detections down to"
                  hint="Nothing is blurred at this score — it only sets how far below the blur bar a detection is still written down. The gap between the two is the record of who the model saw and let through; close it and a missed face reads the same as an empty shelf."
                  badge={applied.humanScanConf}
                  value={t.humanScanConf}
                  live={saved?.humanScanConf}
                  onChange={(v) => setT((s) => ({ ...s, humanScanConf: v }))}
                  max={1}
                />
                <Row gutter={8} style={{ marginTop: 12 }}>
                  <Col span={8}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Regions blurred</Text>
                    <div>
                      <Text style={{ fontSize: 20, color: redaction.keep.length ? CRIT : GOOD }}>
                        {redaction.keep.length}
                      </Text>
                    </div>
                  </Col>
                  <Col span={8}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Frame destroyed</Text>
                    <div>
                      <Text
                        style={{
                          fontSize: 20,
                          color: redaction.areaFraction > 0.15 ? CRIT : GOOD,
                        }}
                      >
                        {pct(redaction.areaFraction)}
                      </Text>
                    </div>
                  </Col>
                  <Col span={8}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Let through</Text>
                    <div>
                      <Text style={{ fontSize: 20, color: MUTED }}>{redaction.below.length}</Text>
                    </div>
                  </Col>
                </Row>

                {redaction.areaFraction > 0.15 && (
                  <Alert
                    type="error"
                    showIcon
                    style={{ marginTop: 12 }}
                    message={`${pct(redaction.areaFraction)} of this frame would be destroyed`}
                    description="Check the overlay before settling here. This model false-positives on bare shelving, and the blur is applied in place at ingest — whatever it covers is not recoverable."
                  />
                )}

                {redaction.droppedDegenerate > 0 && (
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginTop: 12 }}
                    message={`${redaction.droppedDegenerate} box(es) discarded as too thin to be a person`}
                    description="The model fires on the grey letterbox padding the engine adds. These are dropped before any threshold applies, so the slider cannot bring them back."
                  />
                )}

                <Descriptions size="small" column={2} style={{ marginTop: 14 }} bordered>
                  <Descriptions.Item label="Model">{redaction.model || "—"}</Descriptions.Item>
                  <Descriptions.Item label="Candidates found">
                    {redaction.keep.length + redaction.below.length}
                  </Descriptions.Item>
                  <Descriptions.Item label="At ingest" span={2}>
                    {result?.recorded?.humanScreenStatus == null
                      ? "not screened"
                      : result.recorded.humanScreenStatus === "failed"
                      ? "screener failed — frame stored unredacted"
                      : `${result.recorded.humanCount ?? 0} region(s), ${pct(
                          result.recorded.humanRedactedAreaFraction
                        )} destroyed, at conf ${num(result.recorded.humanConf, 2)}`}
                  </Descriptions.Item>
                </Descriptions>

                <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 10 }}>
                  Use <Text strong>Apply to production</Text> above to make these live — they
                  take effect on the next frame ingested, with no redeploy. Re-scoring here
                  never alters the stored photo.
                </Text>
              </>
            )}
          </Card>

          <Card size="small" title="Verdict" style={{ marginTop: 16 }}>
            {error && <Alert type="error" showIcon message={error} />}
            {scoring && !error && (
              <div style={{ textAlign: "center", padding: 24 }}>
                <Spin />
              </div>
            )}
            {!scoring && !error && !result && <Empty description="Score a frame to see its numbers" />}
            {!scoring && result && verdict && (
              <>
                <Tag
                  color={verdict.accepted ? "success" : "error"}
                  style={{ fontSize: 14, padding: "4px 12px", marginBottom: 12 }}
                >
                  {verdict.accepted ? "WOULD PASS" : "WOULD BE REJECTED"}
                </Tag>
                {!verdict.maskAvailable && (
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="No mask from this model"
                    description="The chiller model in force reports boxes only, so the mask checks are unavailable — not passed."
                  />
                )}
                {verdict.checks.map((c) => (
                  <Row key={c.key} style={{ padding: "5px 0", borderBottom: "1px solid #26333722" }}>
                    <Col flex="auto">
                      <Text style={{ color: c.pass ? GOOD : CRIT }}>{c.pass ? "✓" : "✗"}</Text>{" "}
                      <Text>{c.label}</Text>
                    </Col>
                    <Col>
                      <Text code>{c.detail}</Text>
                    </Col>
                  </Row>
                ))}

                <Descriptions size="small" column={2} style={{ marginTop: 14 }} bordered>
                  <Descriptions.Item label="Model">
                    {result.reading?.chillerModel || "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Round trip">{result.roundTripMs} ms</Descriptions.Item>
                  <Descriptions.Item label="Recorded at ingest">
                    {result.recorded?.chillerConfidence != null
                      ? `${num(result.recorded.chillerConfidence, 3)} (${result.recorded.chillerModel || "?"})`
                      : "not scored"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Now">
                    {num(result.reading?.confidence, 3)}
                  </Descriptions.Item>
                  {mask && (
                    <>
                      <Descriptions.Item label="Mask fills">{pct(mask.frameFill)}</Descriptions.Item>
                      <Descriptions.Item label="Solidity">{num(mask.solidity)}</Descriptions.Item>
                      <Descriptions.Item label="Cut off at" span={2}>
                        {mask.cutOffSides?.length ? (
                          <Tag color="error">{mask.cutOffSides.join(", ")}</Tag>
                        ) : (
                          <Text type="secondary">nowhere</Text>
                        )}
                      </Descriptions.Item>
                    </>
                  )}
                </Descriptions>

                {result.recorded?.chillerModel &&
                  result.reading?.chillerModel &&
                  result.recorded.chillerModel !== result.reading.chillerModel && (
                    <Alert
                      style={{ marginTop: 12 }}
                      type="warning"
                      showIcon
                      message="Scored by a different model than at ingest"
                      description={`Recorded with ${result.recorded.chillerModel}, re-scored now with ${result.reading.chillerModel}. Trends spanning the swap are not comparing like with like.`}
                    />
                  )}
              </>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

// What each badge means, spelled out on hover. A slider that only records a preference must
// never look like one that gates the fleet.
const BADGE = {
  live: { color: "success", text: "live", title: "Changes production on the next frame" },
  "dms-only": {
    color: "warning",
    text: "DMS only",
    title: "Drives this dashboard's funnel. Planomaster still gates on its own stack-5 env var.",
  },
  simulation: {
    color: "default",
    text: "simulation",
    title: "Nothing in production consumes this yet — saved as a proposal only",
  },
};

// step + fmt default to the 0..1 fraction case every gate used before the sharpness floor,
// which is a large integer score — passing step={n} fmt={Math.round} renders it correctly.
const SliderRow = ({
  label,
  hint,
  value,
  onChange,
  max,
  disabled,
  badge,
  live,
  step = 0.01,
  fmt = (v) => Number(v).toFixed(2),
}) => (
  <div style={{ marginTop: 10, opacity: disabled ? 0.45 : 1 }}>
    <Row>
      <Col flex="auto">
        <Text>{label}</Text>{" "}
        {BADGE[badge] && (
          <Tag color={BADGE[badge].color} title={BADGE[badge].title} style={{ fontSize: 10 }}>
            {BADGE[badge].text}
          </Tag>
        )}
      </Col>
      <Col>
        {live != null && Number(live) !== Number(value) && (
          <Text type="secondary" style={{ fontSize: 11, marginRight: 6 }}>
            live {fmt(live)} →
          </Text>
        )}
        <Text code>{fmt(value)}</Text>
      </Col>
    </Row>
    <Slider
      min={0}
      max={max}
      step={step}
      value={value}
      onChange={onChange}
      disabled={disabled}
      tooltip={{ open: false }}
    />
    {hint && (
      <Text type="secondary" style={{ fontSize: 11, color: MUTED }}>
        {hint}
      </Text>
    )}
  </div>
);

export default FrameQualityBench;
